import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, Bus, CarTaxiFront, CarFront, ChevronDown, Share2, Ticket, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import "./transport.css";
import { BookingHeader, PrimaryBtn, PriceRow } from "./components";
import { fmt } from "./transportData";
import { getTickets, fetchAllTickets } from "./ticketStore";
import useUserId from "@/hooks/useUserId";

const TYPE_META = {
  avion: { Icon: Plane, label: "Avion", color: "#0E7A4F" },
  car: { Icon: Bus, label: "Car interurbain", color: "#15803D" },
  taxi: { Icon: CarTaxiFront, label: "Taxi", color: "#B45309" },
  chauffeur: { Icon: CarFront, label: "Chauffeur privé", color: "#0B3B27" },
};

export default function MesBilletsPage() {
  const navigate = useNavigate();
  const userId = useUserId();
  const [tickets, setTickets] = useState(getTickets());
  const [syncing, setSyncing] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetchAllTickets(userId)
      .then((list) => { if (!cancelled) setTickets(list); })
      .finally(() => { if (!cancelled) setSyncing(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const share = async (t) => {
    const text = `Billet AYA ${t.ref} — ${t.title}`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* annulé */ } }
    else { await navigator.clipboard?.writeText(text); toast.success("Billet copié ✓"); }
  };

  return (
    <div className="tp-page" data-testid="mes-billets-page">
      <BookingHeader title="Mes billets" onBack={() => navigate(-1)} />
      <div className="tp-container">
        {tickets.length === 0 ? (
          <div className="tp-confirm" data-testid="billets-empty">
            <span className="circ" style={{ background: "#fff", border: "2px solid #DCE5DD" }}><Ticket size={30} color="#94A89B" /></span>
            <h2 style={{ fontSize: 16 }}>Aucun billet pour le moment</h2>
            <p>Vos billets d'avion, de car, courses taxi et réservations chauffeur apparaîtront ici.</p>
            <div style={{ height: 16 }} />
            <PrimaryBtn testid="billets-empty-cta" onClick={() => navigate("/search")}>Réserver un transport</PrimaryBtn>
          </div>
        ) : (
          <>
            <div className="tp-sechead" style={{ marginTop: 12 }}>
              <div><h2>{tickets.length} billet{tickets.length > 1 ? "s" : ""}</h2>
                <p style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {syncing ? <><RefreshCw size={9} className="tp-spin" /> Synchronisation…</> : "Synchronisés avec votre compte AYA"}
                </p></div>
            </div>
            {tickets.map((t) => {
              const meta = TYPE_META[t.type] || TYPE_META.avion;
              const open = openId === t.id;
              return (
                <div key={t.id} className="tp-card" style={{ padding: 0, overflow: "hidden" }} data-testid={`billet-${t.id}`}>
                  <button onClick={() => setOpenId(open ? null : t.id)} data-testid={`billet-toggle-${t.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                    <span className="tp-logo" style={{ background: meta.color, width: 34, height: 34, borderRadius: 10 }}><meta.Icon size={16} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ fontSize: 12.5, display: "block" }}>{t.title}</b>
                      <span style={{ fontSize: 10.5, color: "#7B8B7F" }}>{meta.label} · {t.sub}</span>
                    </span>
                    <span style={{ textAlign: "right" }}>
                      <b style={{ fontSize: 12, color: "#0E7A4F", display: "block" }}>{typeof t.price === "number" ? fmt(t.price) : ""}</b>
                      <ChevronDown size={14} color="#A6B2A9" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                    </span>
                  </button>
                  {open && (
                    <div style={{ borderTop: "1.5px dashed #DCE5DD", padding: "13px 12px" }} data-testid={`billet-detail-${t.id}`}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 10 }}>
                        <QRCodeSVG value={t.ref} size={108} level="M" />
                        <b style={{ fontSize: 11.5, marginTop: 7 }}>{t.ref}</b>
                        <span style={{ fontSize: 9.5, color: "#7B8B7F" }}>{new Date(t.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                      </div>
                      {t.details && Object.entries(t.details).map(([k, v]) => <PriceRow key={k} label={k} value={v} />)}
                      <div className="tp-tbtns">
                        <PrimaryBtn ghost testid={`billet-share-${t.id}`} onClick={() => share(t)}><Share2 size={13} /> Partager</PrimaryBtn>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
